import * as Yup from "yup";
import { StationFormShape, CreateStationDto } from "@containers/Station/interfaces";
import { actions as patientActions, selectors as patientSelectors } from "@containers/Patient/store/";
import { actions as hospitalActions, selectors as hospitalSelectors } from "@containers/Hospital/store/";
import { FORM_ERROR_MESSAGES, STATUS } from "@shared/constants";
import { Station, Patient, Hospital, User } from "@shared/models";
import { Option, ORDER_TYPE } from "@shared/interfaces";
import { getFullName, getStationTrackingTimeRange } from "@shared/utils";
import { ONLY_NUMBERS_REGEXP } from "@shared/constants/regexpMatches";

const SHD_CODE_ERROR_MESSAGE = `SDH ID should be 9 digits.`;

export const commonValidationSchema = {
  station_code: Yup.string()
    .matches(ONLY_NUMBERS_REGEXP, SHD_CODE_ERROR_MESSAGE)
    .min(9, SHD_CODE_ERROR_MESSAGE)
    .max(9, SHD_CODE_ERROR_MESSAGE)
    .required(FORM_ERROR_MESSAGES.REQUIRED),
  qr_code: Yup.string().optional(),
  sensors: Yup.array().of(
    Yup.object().shape({
      value: Yup.string().required(FORM_ERROR_MESSAGES.REQUIRED),
      label: Yup.string().required(FORM_ERROR_MESSAGES.REQUIRED),
    }),
  ),
  patient: Yup.object()
    .shape({
      value: Yup.string().required(FORM_ERROR_MESSAGES.REQUIRED),
      label: Yup.string().required(FORM_ERROR_MESSAGES.REQUIRED),
    })
    .nullable(),
  hospital: Yup.object()
    .shape({
      value: Yup.string().required(FORM_ERROR_MESSAGES.REQUIRED),
      label: Yup.string().required(FORM_ERROR_MESSAGES.REQUIRED),
    })
    .required(),
  start_time: Yup.date().nullable(),
  end_time: Yup.date().nullable(),
  timezone: Yup.string().optional().nullable(),
  firmware_version: Yup.string().optional(),
};

const MIN_TRACKING_HOURS = 4;
const MIN_TRACKING_HOURS_ERROR = `Tracking time range must be at least ${MIN_TRACKING_HOURS} hours`;

const getTrackingDurationHours = (start: Date, end: Date): number => {
  const startMinutes = start.getHours() * 60 + start.getMinutes();
  const endMinutes = end.getHours() * 60 + end.getMinutes();
  // Handle overnight ranges (e.g. 22:00 - 02:00)
  const durationMinutes = endMinutes >= startMinutes ? endMinutes - startMinutes : 24 * 60 - startMinutes + endMinutes;
  return durationMinutes / 60;
};

const getValidationSchema = (shema: Yup.SchemaOf<StationFormShape>) => {
  return shema
    .test("at-least-one", "At least one of start time, end time, or timezone is required", function (value) {
      const { start_time, end_time, timezone } = value;
      const atLeastOneFilled = !!start_time || !!end_time || !!timezone;
      if (!atLeastOneFilled) {
        return true;
      }

      return !!start_time && !!end_time && !!timezone;
    })
    .test("required-fields", "Start time is required if end time or timezone is selected", function (value) {
      const { start_time, end_time, timezone } = value;
      const isEndTimeSelected = !!end_time || !!timezone;
      if (isEndTimeSelected && !start_time) {
        return this.createError({
          path: "start_time",
          message: FORM_ERROR_MESSAGES.REQUIRED,
        });
      }
      return true;
    })
    .test("required-fields-end", "End time is required if start time or timezone is selected", function (value) {
      const { start_time, end_time, timezone } = value;
      const isStartTimeSelected = !!start_time || !!timezone;
      if (isStartTimeSelected && !end_time) {
        return this.createError({
          path: "end_time",
          message: FORM_ERROR_MESSAGES.REQUIRED,
        });
      }
      return true;
    })
    .test("required-fields-timezone", "Timezone is required if start time or end time is selected", function (value) {
      const { start_time, end_time, timezone } = value;
      const isAnyTimeSelected = !!start_time || !!end_time;
      if (isAnyTimeSelected && !timezone) {
        return this.createError({
          path: "timezone",
          message: FORM_ERROR_MESSAGES.REQUIRED,
        });
      }
      return true;
    })
    .test("min-tracking-hours", MIN_TRACKING_HOURS_ERROR, function (value) {
      const { start_time, end_time } = value;
      if (!start_time || !end_time) {
        return true;
      }
      if (getTrackingDurationHours(start_time, end_time) < MIN_TRACKING_HOURS) {
        return this.createError({
          path: "start_time",
          message: MIN_TRACKING_HOURS_ERROR,
        });
      }
      return true;
    });
};

export const createValidationSchema = getValidationSchema(
  Yup.object().shape({
    ...commonValidationSchema,
  }),
);

export const editValidationSchemaSchema: Yup.SchemaOf<StationFormShape> = getValidationSchema(
  Yup.object().shape({
    ...commonValidationSchema,
  }),
);

const timeChangeHandler = (timeDate: Date) => {
  const hours = timeDate.getHours();
  const minutes = timeDate.getMinutes();
  const hoursString = hours < 10 ? `0${hours}` : hours;
  const minutesString = minutes < 10 ? `0${minutes}` : minutes;
  return `${hoursString}:${minutesString}:00`;
};

export const getInitValues = (station: Station | null, defaultHospitalId: number | null): StationFormShape => {
  const getHospitalOption = (hospital?: Hospital | null): Option | null => {
    if (!hospital) {
      return {
        value: String(defaultHospitalId),
        label: "Default hospital",
      };
    }

    return {
      value: String(hospital.id),
      label: hospital.name,
    };
  };

  const getPatientOption = (patient?: User | null): Option | null => {
    if (!patient) {
      return null;
    }

    return {
      value: String(patient.id),
      label: getFullName(patient),
    };
  };

  const { endTime, startTime, timezone } = getStationTrackingTimeRange(station);

  return {
    station_code: station?.station_code || "",
    patient: getPatientOption(station?.patient),
    hospital: getHospitalOption(station?.hospital),
    firmware_version: station?.firmware_version || "",
    qr_code: station?.qr_code || "",
    end_time: endTime,
    start_time: startTime,
    timezone: timezone,
  };
};

export const getHandlers = (hospital: Option | null) => {
  return {
    patient: {
      getData: (search: string) =>
        patientActions.getPatients.request({
          search,
          page: 0,
          limit: 20,
          status: STATUS.ACTIVE,
          hospital,
          sort_by: ["first_name", "last_name"],
          sort_order: ORDER_TYPE.ASC,
        }),
      selectData: patientSelectors.getPatients,
      prepareOptionFunction: (item: Patient): Option => {
        return { label: getFullName(item) || item.login, value: String(item.id) };
      },
      loadingAction: patientActions.getPatients,
    },
    hospital: {
      getData: (search: string) =>
        hospitalActions.getHospitals.request({
          search,
          page: 0,
          limit: 20,
        }),
      selectData: hospitalSelectors.getHospitals,
      prepareOptionFunction: (item: Hospital): Option => {
        return { label: item.name, value: String(item.id) };
      },
      loadingAction: hospitalActions.getHospitals,
    },
  };
};

export const prepareSubmitValue = (value: StationFormShape): CreateStationDto => ({
  station_code: value.station_code,
  patient_id: value.patient ? Number(value.patient.value) : null,
  firmware_version: value.firmware_version,
  hospital_id: value.hospital ? Number(value.hospital.value) : null,
  end_time: value.end_time ? timeChangeHandler(value.end_time) : null,
  start_time: value.start_time ? timeChangeHandler(value.start_time) : null,
  timezone: value.timezone || null,
  qr_code: value.qr_code || "",
});
